import { App, Empty, Flex } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { getApiError, postOrderClose, postOrderSave } from "tm-shared/api";
import { OrderType, WorkStatus } from "tm-shared/types";

import { ROUTES } from "@/constants";
import { useAsyncAction, useQueueInfo } from "@/hooks";
import { QueueTaskStatus } from "@/hooks/core/useAsyncTaskQueue";
import { useGlobalCooldown } from "@/hooks/core/useGlobalCooldown";
import {
    useAppDispatch,
    useAppSelector,
    useOfflineModeSelector,
    useProtectedUserRoleSelector,
} from "@/hooks/redux";
import { Header } from "@/shared/layout";
import { HeaderReloadBtn } from "@/shared/navigation/HeaderReloadBtn";
import { BlockList } from "@/shared/pageComposition/BlockList";
import { Meta } from "@/shared/seo";
import { setCurrentRoute } from "@/store/slices/manager";
import { removeIgnoreRemainingOrders } from "@/store/slices/offline/offline.slice";
import {
    getCheckoutsThunk,
    setCheckoutActiveCollapse,
} from "@/store/slices/ui";

import { CheckoutModel, getCheckoutBlocks } from "./Checkout.blocks";
import { CheckoutProvider } from "./Checkout.context";
import { getCheckoutCapabilities } from "./Checkout.policy";
import { StyledCheckoutMainContainer } from "./Styled";
import {
    convertCheckoutListToMarketProducts,
    processPaymentForm,
} from "./utils";

function Checkout() {
    const { notification } = App.useApp();
    const { routeId: routeIdParam = "" } = useParams();
    const dispatch = useAppDispatch();

    const [isConfirmModalOpened, setIsConfirmModalOpened] = useState(false);
    const [checkedOrderIds, setCheckedOrderIds] = useState<Set<string>>(
        new Set(),
    );

    const checkoutList = useAppSelector((state) => state.work.checkoutList);
    const role = useProtectedUserRoleSelector();
    const userId = useAppSelector((s) => s.user.id);
    const route = useAppSelector((s) => s.user.route);

    const checkoutAgentsList = useAppSelector(
        (state) => state.manager.checkoutAgentsList,
    );
    const routeList = useAppSelector((state) => state.manager.routeList);

    const checkoutDetails = useAppSelector(
        (state) => state.progress.checkoutDetails,
    );
    const workStatus = useAppSelector((state) => state.work.workStatus);
    const requestQueue = useAppSelector((state) => state.offline.requestQueue);
    const ignoreRemainingsOrders = useAppSelector(
        (state) => state.offline.ignoreRemainingsOrders,
    );
    const offlineMode = useOfflineModeSelector();

    const resetCheckoutsLoading = useAppSelector(
        (state) => state.ui.checkout.loading,
    );

    const { disabled } = useGlobalCooldown(30_000);
    const { isRunning: isRunningQueue } = useQueueInfo();

    const capabilities = useMemo(() => getCheckoutCapabilities(role), [role]);

    const isAnyOrderChosen = useMemo(
        () => checkedOrderIds.size > 0,
        [checkedOrderIds.size],
    );

    const closeConfirmModal = useCallback(() => {
        setIsConfirmModalOpened(false);
    }, []);

    const handleResetCheckouts = useCallback(async () => {
        setCheckedOrderIds(new Set());

        const routeId = capabilities.isManager
            ? routeIdParam
            : route?.uuid ?? "";

        try {
            await dispatch(
                getCheckoutsThunk({
                    routeId,
                    strict: true,
                }),
            ).unwrap();
        } catch (error) {
            const { displayMessage } = getApiError(error);

            notification.error({
                title: "Помилка при оновленні кошика",
                description: displayMessage,
            });
        }
    }, [
        capabilities.isManager,
        dispatch,
        notification,
        route?.uuid,
        routeIdParam,
    ]);

    const [handleCloseOrder, isProcessingClosingOrder] = useAsyncAction(
        async () => {
            const filtered = Object.fromEntries(
                Object.entries(checkoutList).filter(
                    ([orderId, order]) =>
                        checkedOrderIds.has(orderId) &&
                        order.orderType !== OrderType.P &&
                        order.orderType !== OrderType.R,
                ),
            );

            if (Object.keys(filtered).length) {
                try {
                    const request = convertCheckoutListToMarketProducts(
                        filtered,
                        ignoreRemainingsOrders,
                    );

                    await postOrderSave(request);
                } catch (error) {
                    const { displayMessage } = getApiError(error);

                    notification.error({ description: displayMessage });

                    return;
                }
            }

            const trueIds = Array.from(checkedOrderIds);

            const details = trueIds
                .map((orderId) => {
                    const checkout = checkoutList[orderId];

                    if (!checkout) return [];

                    const paymentForm = checkout.paymentForm;
                    const {
                        isCarrier,
                        comment,
                        commentChecked,
                        isPayOnDelivery,
                        isSelfPickup,
                    } = checkoutDetails[orderId] ?? {};

                    return processPaymentForm(
                        {
                            comment,
                            commentChecked,
                            isCarrier,
                            isSelfDelivery: isSelfPickup,
                            isPayOnDelivery,
                        },
                        orderId,
                        paymentForm,
                        checkout.customShipmentDate === checkout.shipmentDate
                            ? ""
                            : checkout.customShipmentDate,
                    );
                })
                .flat();

            const agentId = capabilities.isManager
                ? routeList[routeIdParam]?.agentInfo?.id
                : userId;

            if (!agentId) {
                notification.error({ title: "Відсутнє значення agentId" });

                return;
            }

            try {
                await postOrderClose({
                    agentId,
                    orderIds: trueIds,
                    details,
                });

                notification.success({ title: "Замовлення оформлено" });

                dispatch(removeIgnoreRemainingOrders(trueIds));
                dispatch(setCheckoutActiveCollapse(null));

                await handleResetCheckouts();
            } catch (error) {
                const { displayMessage } = getApiError(error);

                notification.error({
                    title: "Помилка при оформленні замовлення",
                    description: displayMessage,
                });
            }

            closeConfirmModal();
        },
    );

    const orderSaveQueue = useMemo(() => {
        return requestQueue?.filter((item) => {
            if (item.status === QueueTaskStatus.Error) return false;

            const checkoutCommands = [
                "postOrderSave",
                "postOrderSaveOne",
                "updateCheckout",
            ];

            return item.commands.some((cmd) =>
                checkoutCommands.includes(cmd.type),
            );
        });
    }, [requestQueue]);

    const uniqueOrdersCount = useMemo(() => {
        if (!orderSaveQueue?.length) return 0;

        const uniqueIds = new Set(
            orderSaveQueue
                .map((item) => item?.metadata?.orderId)
                .filter(Boolean),
        );

        return uniqueIds.size;
    }, [orderSaveQueue]);

    useEffect(() => {
        if (capabilities.isManager && !offlineMode) {
            dispatch(setCurrentRoute(routeIdParam));
            handleResetCheckouts();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const hasPendingQueue = orderSaveQueue.length > 0;
    const isQueueBlocked = !isRunningQueue && hasPendingQueue && !offlineMode;
    const isQueueSyncing = isRunningQueue && hasPendingQueue;
    const isCheckoutAvailable = !hasPendingQueue || offlineMode;

    const onToggleOrderId = useCallback((orderId: string) => {
        setCheckedOrderIds((prev) => {
            const next = new Set(prev);
            next.has(orderId) ? next.delete(orderId) : next.add(orderId);
            return next;
        });
    }, []);

    const model: CheckoutModel = useMemo(
        () => ({
            queue: {
                isQueueBlocked,
                isQueueSyncing,
                uniqueOrdersCount,
            },
            checkout: {
                isCheckoutAvailable,
                resetCheckoutsLoading,
                isProcessingClosingOrder,
                checkedOrderIds,
                isAnyOrderChosen,
                offlineMode,
                onToggleOrderId,
                onResetCheckouts: handleResetCheckouts,
                onOpenConfirmModal: () => setIsConfirmModalOpened(true),
            },
            modal: {
                isConfirmModalOpened,
                isProcessingClosingOrder,
                isAnyOrderChosen,
                onClose: closeConfirmModal,
                onConfirm: handleCloseOrder,
            },
        }),
        [
            checkedOrderIds,
            closeConfirmModal,
            handleCloseOrder,
            handleResetCheckouts,
            isAnyOrderChosen,
            isCheckoutAvailable,
            isConfirmModalOpened,
            isProcessingClosingOrder,
            isQueueBlocked,
            isQueueSyncing,
            offlineMode,
            onToggleOrderId,
            resetCheckoutsLoading,
            uniqueOrdersCount,
        ],
    );

    const blocks = useMemo(() => getCheckoutBlocks(model), [model]);

    const canRenderCheckoutContent =
        workStatus === WorkStatus.StartWork ||
        capabilities.canBypassWorkStatusCheck;

    return (
        <CheckoutProvider value={{ capabilities }}>
            <Meta title="Checkout" />
            <Header
                path={
                    capabilities.isManager && !offlineMode
                        ? ROUTES.SVCheckout
                        : undefined
                }
                titleLoading={resetCheckoutsLoading}
                title={
                    capabilities.isManager
                        ? checkoutAgentsList.find(
                              (item) => item.routeId === routeIdParam,
                          )?.userName
                        : ""
                }
                rightActions={
                    <HeaderReloadBtn
                        onClick={handleResetCheckouts}
                        disabled={disabled || offlineMode}
                    />
                }
            />

            <StyledCheckoutMainContainer>
                {canRenderCheckoutContent ? (
                    <BlockList blocks={blocks} model={model} />
                ) : (
                    <Flex justify="center" align="center">
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="Робочий день не розпочато"
                        />
                    </Flex>
                )}
            </StyledCheckoutMainContainer>
        </CheckoutProvider>
    );
}

export default Checkout;
